import type { Theme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-premium';

import {
  formatInvestorHomePercentage,
  formatInvestorLastLogin,
  formatInvestorNetWorth,
  type TranslateFn,
} from '@/modules/clientServices/hooks/useClientColumns';
import type { AdvisorInvestor } from '@/store/usersApi';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatAppDate } from '@/utils/date';

export type TextMeasurer = {
  cell: (text: string) => number;
  header: (text: string) => number;
};

/** 8px each side, the same as the cell and header padding in GRID_SX. */
export const CELL_HORIZONTAL_PADDING_PX = 16;

/**
 * Gap between the header title and the sort icon container. The gap applies
 * while that container is width 0 and hidden, so the title has this much less
 * than the header width. The app theme sets it to 8px for every grid; GRID_SX
 * sets it to this value for the widget so the fit and the CSS agree.
 */
export const HEADER_TITLE_GAP_PX = 2;

/**
 * Width the sort icon container takes beside the header title. MUI gives it
 * zero width while hidden and its full width once the column is sorted,
 * filtered or hovered, which reflows a fitted header. GRID_SX pins it to this
 * width in every state, and the fit reserves the same amount, so the header
 * keeps its line count whatever the sort state is. The value is the icon size
 * the app theme gives every grid icon button, with the button padding removed.
 */
export const SORT_ICON_WIDTH_PX = 20;

export const HEADER_HEIGHT_PX = 40;
export const HEADER_FONT_SIZE_PX = 14;
export const HEADER_LINE_HEIGHT = 1.2;

/**
 * Lines a wrapped header can show before the fixed header height clips it.
 * The grid sets the three values above from these constants so the budget
 * and the CSS agree.
 */
export const HEADER_MAX_LINES = Math.floor(
  HEADER_HEIGHT_PX / (HEADER_FONT_SIZE_PX * HEADER_LINE_HEIGHT),
);

const ROUNDING_SLACK_PX = 2;

/**
 * The fitted columns and the text each cell renders, so what is measured is
 * what is shown. Each formatter is the one the column uses in
 * useClientColumns. `Home` is AUM.
 */
const FITTED_CELL_TEXT: Partial<
  Record<string, (row: AdvisorInvestor, t: TranslateFn) => string>
> = {
  Name: (row) => row.Name,
  UserName: (row) => row.UserName ?? '',
  NetWorth: formatInvestorNetWorth,
  Home: (row) =>
    formatNumberToCurrency(typeof row.Home === 'number' ? row.Home : 0),
  HomePercentage: formatInvestorHomePercentage,
  LastLogin: formatInvestorLastLogin,
  LastOrionLogin: (row) => formatAppDate(row.LastOrionLogin, { fallback: '-' }),
};

/**
 * The text columns. Their headers do not wrap, so the whole label is measured,
 * and the widget applies their fit as a floor under a flex width rather than
 * as the width itself.
 */
const TEXT_COLUMN_FIELDS: ReadonlySet<string> = new Set(['Name', 'UserName']);

/**
 * One long email would otherwise widen Username for every row. Beyond this the
 * cell truncates. Name has no cap: a clipped name is the case the fit exists
 * to prevent.
 */
export const FITTED_WIDTH_CAP_PX: Partial<Record<string, number>> = {
  UserName: 320,
};

const wrappedLineCount = (
  words: ReadonlyArray<string>,
  width: number,
  measureHeader: TextMeasurer['header'],
): number => {
  let lines = 0;
  let start = 0;
  while (start < words.length) {
    if (measureHeader(words[start]) > width) return Number.POSITIVE_INFINITY;
    let end = start + 1;
    while (
      end < words.length &&
      measureHeader(words.slice(start, end + 1).join(' ')) <= width
    ) {
      end += 1;
    }
    lines += 1;
    start = end;
  }
  return lines;
};

/**
 * Narrowest width at which the browser's greedy wrap keeps the label within
 * HEADER_MAX_LINES. Every run of consecutive words is a candidate line, and
 * the whole label is always one of them, so a result always exists.
 */
const narrowestWrappedHeaderWidth = (
  label: string,
  measureHeader: TextMeasurer['header'],
): number => {
  const words = label.split(/\s+/).filter((word) => word !== '');
  if (words.length === 0) return 0;

  const candidates = new Set<number>();
  for (let start = 0; start < words.length; start += 1) {
    for (let end = start + 1; end <= words.length; end += 1) {
      candidates.add(measureHeader(words.slice(start, end).join(' ')));
    }
  }
  const sorted = [...candidates].sort((a, b) => a - b);
  return (
    sorted.find(
      (width) =>
        wrappedLineCount(words, width, measureHeader) <= HEADER_MAX_LINES,
    ) ?? measureHeader(label)
  );
};

const headerWidth = (
  column: GridColDef<AdvisorInvestor>,
  measureHeader: TextMeasurer['header'],
): number => {
  const label = column.headerName ?? '';
  return TEXT_COLUMN_FIELDS.has(column.field)
    ? measureHeader(label)
    : narrowestWrappedHeaderWidth(label, measureHeader);
};

/**
 * Width each fitted column needs to show its widest value and its header
 * without an ellipsis. A number or date header wraps at spaces (see
 * COLUMN_SIZING), so it contributes the narrowest width that keeps it within
 * the header height, plus the title gap, not the whole label.
 */
export const fitClientColumnWidths = (
  columns: ReadonlyArray<GridColDef<AdvisorInvestor>>,
  rows: ReadonlyArray<AdvisorInvestor>,
  measure: TextMeasurer,
  t: TranslateFn,
): Partial<Record<string, number>> => {
  const widths: Partial<Record<string, number>> = {};

  for (const column of columns) {
    const cellText = FITTED_CELL_TEXT[column.field];
    if (cellText === undefined) continue;

    let widest =
      headerWidth(column, measure.header) +
      HEADER_TITLE_GAP_PX +
      SORT_ICON_WIDTH_PX;
    for (const row of rows) {
      widest = Math.max(widest, measure.cell(cellText(row, t)));
    }
    const fitted =
      Math.ceil(widest) + CELL_HORIZONTAL_PADDING_PX + ROUNDING_SLACK_PX;
    const cap = FITTED_WIDTH_CAP_PX[column.field];
    widths[column.field] = cap === undefined ? fitted : Math.min(fitted, cap);
  }

  return widths;
};

export type ClientTableLayout = 'fitted' | 'compact' | 'cards';

export type ClientTableWidths = { fittedPx: number; compactPx: number };

/**
 * Width MUI takes back from the columns for the vertical scrollbar once the
 * rows overflow the grid height.
 */
export const TABLE_WIDTH_SLACK_PX = 16;

const MUI_DEFAULT_COLUMN_WIDTH_PX = 100;
const MUI_DEFAULT_COLUMN_MIN_WIDTH_PX = 50;

/**
 * The narrowest row the table renders in each layout. MUI never renders a flex
 * column below its minWidth, so the row is the sum of the fixed widths and the
 * flex floors, and a grid narrower than that scrolls sideways. The fitted
 * layout floors the text columns at their fitted width, the compact layout at
 * the static minWidth from COLUMN_SIZING.
 */
export const clientTableWidths = (
  columns: ReadonlyArray<GridColDef<AdvisorInvestor>>,
  fittedWidths: Partial<Record<string, number>>,
): ClientTableWidths => {
  let fittedPx = 0;
  let compactPx = 0;
  for (const column of columns) {
    const fitted = fittedWidths[column.field];
    if (column.flex) {
      const floor = column.minWidth ?? MUI_DEFAULT_COLUMN_MIN_WIDTH_PX;
      fittedPx += fitted ?? floor;
      compactPx += floor;
    } else {
      const width = fitted ?? column.width ?? MUI_DEFAULT_COLUMN_WIDTH_PX;
      fittedPx += width;
      compactPx += width;
    }
  }
  return { fittedPx, compactPx };
};

/**
 * The widest layout that fits the measured grid width. An unknown width, on
 * the first paint or in a container the widget does not measure, keeps the
 * fitted layout.
 */
export const selectClientTableLayout = (
  widthPx: number | null,
  widths: ClientTableWidths,
): ClientTableLayout => {
  if (widthPx === null || !Number.isFinite(widthPx)) return 'fitted';
  const available = widthPx - TABLE_WIDTH_SLACK_PX;
  if (available >= widths.fittedPx) return 'fitted';
  if (available >= widths.compactPx) return 'compact';
  return 'cards';
};

const toPx = (
  size: string | number | undefined,
  htmlFontSize: number,
): string => {
  if (typeof size === 'number') return `${String(size)}px`;
  if (size === undefined) return '14px';
  if (size.endsWith('rem')) {
    return `${String(parseFloat(size) * htmlFontSize)}px`;
  }
  return size;
};

/**
 * Letter spacing the grid text inherits. CssBaseline gives body the body1
 * letter spacing, and the grid resets its text with the `font` shorthand,
 * which does not cover letter spacing. Canvas measureText ignores it, so it
 * is added per glyph.
 */
const inheritedLetterSpacingPx = (): number => {
  const spacing = parseFloat(getComputedStyle(document.body).letterSpacing);
  return Number.isFinite(spacing) ? spacing : 0;
};

/**
 * Canvas text measurer in the grid's cell and header fonts. Returns null where
 * canvas is unavailable, such as jsdom, and the caller falls back to fixed
 * widths.
 */
export const createClientGridTextMeasurer = (
  theme: Theme,
  headerFontWeight: number,
): TextMeasurer | null => {
  if (typeof document === 'undefined') return null;
  const context = document.createElement('canvas').getContext('2d');
  if (!context) return null;

  const fontSize = toPx(
    theme.typography.body2.fontSize,
    theme.typography.htmlFontSize,
  );
  const fontFamily = theme.typography.fontFamily ?? 'sans-serif';
  const cellFont = `400 ${fontSize} ${fontFamily}`;
  const headerFont = `${String(headerFontWeight)} ${fontSize} ${fontFamily}`;

  const letterSpacingPx = inheritedLetterSpacingPx();
  const measureWith = (font: string) => (text: string) => {
    context.font = font;
    return context.measureText(text).width + letterSpacingPx * text.length;
  };

  return { cell: measureWith(cellFont), header: measureWith(headerFont) };
};
